import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EmailPreviewComponent } from '../email-preview/email-preview.component';
import { EmailData } from '../../services/email-preview.service';

/**
 * Modal dialog component that previews an email in either a desktop or mobile layout, allowing users to close the modal or open the email in a browser for full view.
 * @component
 * @selector app-email-preview-modal
 * @example
 *   <app-email-preview-modal [emailData]="sampleEmail" (closeModal)="onClose()" (viewInBrowser)="onViewInBrowser()"></app-email-preview-modal>
 * @input EmailData | null emailData - Optional email data used to populate the preview. If not provided, the component falls back to an internal defaultEmailData containing sample subject, header, body text, button, and footer information.
 * @output EventEmitter<void> closeModal - Emitted when the modal should be closed (e.g., backdrop click or explicit close button). Parent components should listen to this to hide or destroy the modal.
 * @output EventEmitter<void> viewInBrowser - Emitted when the user chooses to view the email in a browser (e.g., "View in Browser" action). Parent components can handle opening a new tab or external preview.
 *
 * Key methods:
 * - currentEmailData(): getter that returns the provided emailData or the defaultEmailData if none is supplied, ensuring the preview always has data to render.
 * - onBackdropClick(event: Event): closes the modal when the backdrop itself is clicked (only when event.target === event.currentTarget), preventing accidental closes when interacting with modal content.
 * - onCloseClick(): emits closeModal to signal the parent to close the modal.
 * - switchTab(tab: 'desktop' | 'mobile'): toggles the activeTab state to change the preview between desktop and mobile renderings.
 * - onViewInBrowser(): emits viewInBrowser to request that the parent open the email in a browser or separate view.
 *
 * Lifecycle and behavior notes:
 * - Standalone, presentational component that does not implement Angular lifecycle hooks directly; it relies on input changes and output events to interact with parent components.
 * - Maintains an internal activeTab state ('desktop' | 'mobile') for UI tab switching.
 * - Provides a complete defaultEmailData object so the preview is usable even without externally supplied emailData.
 */
@Component({
  selector: 'app-email-preview-modal',
  standalone: true,
  imports: [CommonModule, EmailPreviewComponent],
  templateUrl: './email-preview-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./email-preview-modal.component.scss'],
})
export class EmailPreviewModalComponent {
  @Input() emailData: EmailData | null = null;

  @Output() closeModal = new EventEmitter<void>();
  @Output() viewInBrowser = new EventEmitter<void>();

  // Tab switching state
  activeTab: 'desktop' | 'mobile' = 'desktop';

  // Default email data for preview
  defaultEmailData: EmailData = {
    // Basic Info
    subject: '50% off your next car wash this Black Friday!',
    fromEmail: 'noreply@carwash.com',

    // Header
    logo: null,

    // Body
    image: null,
    header: '50% off your next car wash this Black Friday!',
    bodyText:
      'Customer_Name, this black friday we are giving you 50% off on these select washes every day of this month. Hurry now!',
    body2Text: 'Pristine Wash\nClean Wash\nSpot Free Wash',
    body3Text: '',
    body4Text: '',

    // Button
    buttonText: 'Buy Now',
    buttonPosition: 'bottom',
    buttonColor: '#326FD1',

    // Footer
    carWashName: 'ABC CAR WASH',
    street: '55 street',
    city: 'Jacksonville',
    state: 'Texas',
    zipCode: '8123897',
    email: 'youremail@email.com',
    phoneNumber: '+1 01 1291230',
  };

  get currentEmailData(): EmailData {
    return this.emailData || this.defaultEmailData;
  }

  onBackdropClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.closeModal.emit();
    }
  }

  onCloseClick(): void {
    this.closeModal.emit();
  }

  switchTab(tab: 'desktop' | 'mobile'): void {
    this.activeTab = tab;
  }

  onViewInBrowser(): void {
    this.viewInBrowser.emit();
  }
}
